import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute, Params } from "@angular/router";
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import { MatDatepicker, MatCalendarView, MatDatepickerInput } from "@angular/material/datepicker";
import { DatePipe, CurrencyPipe } from "@angular/common"
import { NgxChartsModule, LegendPosition } from "@swimlane/ngx-charts";
import { DateTime } from "luxon";

import { environment } from "../../../environments/environment";
import { AbstractBuyerComponent } from "../abstract/buyer.component";
import { Pair } from "../../obj/Pair";
import { AccountSelectData } from "../../obj/AccountSelectData";
import { AuthService } from "../../service/auth.service";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { EntityBase } from "../../entity/EntityBase";
import { User } from "../../entity/User";
import { CurrentContractData } from "../../obj/CurrentContractData";
import { Account } from "../../entity/Account";
import { FuelType } from "../../entity/FuelType";
import { BuyOffer } from "../../entity/BuyOffer";
import { SellOffer } from "../../entity/SellOffer";
import { Utility } from "../../entity/Utility";
import { LoadZone } from "../../entity/LoadZone";
import { Contract } from "../../entity/Contract";
import { RateData } from "../../obj/RateData";
import { NgxMaskDirective } from "ngx-mask";
import { KilowantBarPlaceOrderChartComponent } from "../chart/kilowant-charts-place-order.component";

export const MY_FORMATS = {
    parse: {
        dateInput: "MM/dd/yyyy",
    },
    display: {
        dateInput: "MM/dd/yyyy",
        monthYearLabel: "MMM yyyy",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM yyyy",
    },
};

@Component({
    selector: "kilowant-buy-offer-edit",
    templateUrl: "../../../html/incs/buy-offer-edit.html",
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatDatepickerInput, FormsModule, ReactiveFormsModule, MatDatepicker, NgxMaskDirective, KilowantBarPlaceOrderChartComponent, CurrencyPipe, DatePipe]
})
export class BuyOfferEditComponent extends AbstractBuyerComponent {
    @Output() finished = new EventEmitter<boolean>();
    @Output() cancelled = new EventEmitter<boolean>();
    @ViewChild("body") bodyElement: ElementRef;
    @ViewChild("multiSelect") multiSelect: any;

    id: number = 0;
    @Input("id") set setValue(value: number) {
        this.id = value;
    }

    dropdownSettings: IDropdownSettings;

    desiredStartTerm = new UntypedFormControl();
    desiredEndTerm = new UntypedFormControl();
    durationOfOrder = new UntypedFormControl();

    showError: boolean = false;
    error: string = "";
    errors: string[] = [];
    selectedAccounts: Account[] = [];
    accounts: Account[] = [];
    accountsSelector: AccountSelectData[] = [];
    step: number = 1;
    processCompleted: boolean = false;
    processReview: boolean = false;
    buyOffer: BuyOffer = new BuyOffer();
    desiredPrice: number | null = null;
    desiredTerm: string | null = null;
    filesToUpload: any;
    utility: Utility = null!;
    loadZone: LoadZone | null = null;
    utilities: Utility[] = [];

    currentCost: CurrentContractData | null = null;
    latestUtility: number | null = null;
    globalStart: DateTime;
    globalEnd: DateTime;

    disabledButtons: string = "";

    latestUtilityDate: string | null = null;
    latestCommunityDate: string | null = null;

    benchmarkType: string | null = null;
    benchmarkOffer: SellOffer = null!;
    benchmarkContract: Contract = null!;
    customMax: number = 0;

    useSuggested: boolean;

    multi: any[] = [];
    activeRates: any[] = [];
    ratesAveragePrice: number = 0;
    view: [number, number] = [400, 300];
    legend: boolean = true;
    legendTitle: string = "";
    legendPosition: LegendPosition = null!;
    showLabels: boolean = true;
    animations: boolean = true;
    xAxis: boolean = true;
    yAxis: boolean = true;
    showYAxisLabel: boolean = false;
    showXAxisLabel: boolean = false;
    xAxisLabel: string = "Year";
    yAxisLabel: string = "";
    colorScheme = { domain: ["#D0D0D0", "#A0A0A0"], name: "kilowant", selectable: true, group: null! };
    accountsRegex = /^([\d\-]{4,}(,\s*)?)+$/g;

    selectedDatasets: number[] = [];

    constructor(
        router: Router,
        route: ActivatedRoute,
        authService: AuthService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        dialog: MatDialog,
        private datepipe: DatePipe
    ) { super(router, route, dialog, authService, analyticsService, generalService); }

    ngOnInit(): void {
        super.ngOnInit();
    }

    pageStart(): void {
        this.globalStart = this.currentDate.plus({ days: 14 });
        this.globalEnd = this.currentDate.plus({ years: 5 });
        if ((null == this.id) || (this.id == 0)) {
            var d = this.currentDate.plus({ days: 14 }).startOf("month");
            if (d < (this.currentDate).plus({ days: 14 })) {
                d = d.plus({ months: 1 });
            }
            this.desiredStartTerm = new UntypedFormControl(d);
            this.desiredEndTerm = new UntypedFormControl();
            let ctrlValue = this.desiredStartTerm.value;
            ctrlValue = ctrlValue.set({ day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });
            this.desiredStartTerm.setValue(ctrlValue);
            this.setDefaultDuration(ctrlValue);
        }
        this.prepareAccounts();
    }

    ngAfterViewInit() {
        if (this.bodyElement != null) {
            this.view[0] = this.bodyElement.nativeElement.offsetWidth;
        }
    }

    prepareAccounts(): void {
        this.generalService.getAccounts(true)
            .then(accounts => {
                this.accounts = accounts;
                this.accountsSelector = this.generateAccountsSelect(this.accounts, (x: any) => { return x; });
                this.utilities = this._getUtilities(this.accounts);
                this.utilities = [];
                if (this.utilities.length == 1) {
                    this.utility = this.utilities[0];
                }

                this.route.queryParams.subscribe((params:any) => {
                    var benchmarkOfferId = params.benchmarkOffer;
                    var selectedAccountsIds = params.accounts;
                    this.useSuggested = (params.suggested != null);
                    if (benchmarkOfferId != null) {
                        this.generalService.getSellOfferPerAccount(null, benchmarkOfferId).then(offerPair => {
                            this.benchmarkOffer = offerPair.key;
                            this.benchmarkType = "sell_offer-10";
                            this.utility = this.benchmarkOffer.utility;
                            this.resetSelectedAccounts();
                            if (selectedAccountsIds != null) {
                                this.automaticallySelectAccounts(selectedAccountsIds.split(","));
                                var term = DateTime.fromJSDate(this.benchmarkOffer.termFrom);
                                term = term.set({ hour: 0, minute: 0, millisecond: 0 });
                                this.desiredStartTerm.setValue(term);
                                this.desiredEndTerm.setValue(term.plus({ months: this.benchmarkOffer.termPeriod }));
                                this.buyOffer.price = this.benchmarkOffer.priceForBuyer() * 0.9;
                                this.customMax = this.benchmarkOffer.priceForBuyer();
                                this.setDesiredTerm();
                                this.setDefaultDuration(term);
                                this.finishStep1();
                            }
                        });
                    } else if (this.useSuggested) {
                        var account = this.generalService.getStoredAccount();
                        if (account == null) {
                            return;
                        }
                        this.automaticallySelectAccounts([account.id]);

                        if (this.generalService.getStoredSuggestedStartDate()) {
                            this.desiredStartTerm.setValue(this.generalService.getStoredSuggestedStartDate()!);
                        }
                        if (this.generalService.getStoredSuggestedEndDate()) {
                            this.desiredEndTerm.setValue(this.generalService.getStoredSuggestedEndDate()!);
                        }
                        if (this.generalService.getStoredSuggestedPrice()) {
                            this.customMax = this.generalService.getStoredSuggestedPrice()!;
                            this.buyOffer.price = this.generalService.getStoredSuggestedPrice()!;
                        }
                        this.setDesiredTerm();
                        if (this.generalService.getStoredSuggestedStartDate()) {
                            this.setDefaultDuration(this.generalService.getStoredSuggestedStartDate()!);
                        }
                        this.benchmarkType = "none";
                        this.finishStep1();
                    } else {
                        if (!(null == this.id) && this.id > 0) {
                            this.generalService.getBuyOffer(this.id)
                                .then(b => {
                                    this.buyOffer = b.key;
                                    if (this.buyOffer.accounts[0].utility != null) {
                                        this.utility = this.buyOffer.accounts[0].utility;
                                    }
                                    var ids = [];
                                    for (var x = 0; x < this.buyOffer.accounts.length; x++) {
                                        ids.push(this.buyOffer.accounts[x].id);
                                    }
                                    this.automaticallySelectAccounts(ids);

                                    this.desiredStartTerm = new UntypedFormControl(DateTime.fromJSDate(this.buyOffer.termFrom));
                                    this.desiredEndTerm = new UntypedFormControl(DateTime.fromJSDate(this.buyOffer.termFrom).plus({ months: this.buyOffer.termPeriod }));
                                    this.durationOfOrder = new UntypedFormControl(DateTime.fromJSDate(this.buyOffer.validDate));
                                    this.changeChart();
                                    this.setDesiredTerm();
                                    this.benchmarkType = "none";
                                    this.finishStep1();
                                    this.finishStep2();
                                });
                        } else {
                            this.buyOffer.price = 0;
                            this.buyOffer.termPeriod = 12;
                            if (this.accounts.length == 1) {
                                this.automaticallySelectAccounts([this.accounts[0].id]);
                                this.finishStep1();
                                return;
                            }
                            var account = this.generalService.getStoredAccount();
                            if (account != null) {
                                this.automaticallySelectAccounts([account.id]);
                            }
                        }
                    }
                });
            }
            );
    }

    finishStep1(): void {
        this.errors = [];
        if (this.selectedAccounts.length == 0) {
            this.errors.push("selectedAccounts");
            this.showError = true;
            this.error = "Please, select utility accounts.";
            return;
        }
        var period = 3;
        const ctrlValue = this.desiredStartTerm.value;
        ctrlValue.set({ day: 1 });
        this.desiredStartTerm.setValue(ctrlValue);

        let promiseRates = this.generalService.getRates(this.desiredStartTerm.value, null, this.buyOffer.termPeriod, this.selectedAccounts);
        let proposedBuyOfferPrice = this.generalService.getAccountGetLatestUtilityDate(this.selectedAccounts[0].id);

        Promise.all([promiseRates, proposedBuyOfferPrice]).then((data) => {
            if (data[1].key != null) {
                this.latestUtilityDate = this.datepipe.transform(data[1].key, "MMM. y");
            }
            if (data[1].value != null) {
                this.latestCommunityDate = this.datepipe.transform(data[1].value, "MMM. y");
            }
            this.multi = data[0];
            this.ratesAveragePrice = this.generalService.getAverageForRates(this.multi);
            if (this.checkIfUtilityEmpty()) {
                this.setPriceLatestUtilityRate();
            }
            if (this.buyOffer.id == 0 && this.benchmarkOffer == null) {
                this.setPrice();
            }
            this.changeDataset(1);
            this.step = 2;
        });
        this.error = "";
        this.showError = false;
    }

    finishStep2(): void {
        this.errors = [];

        if (this.buyOffer.price == null || this.buyOffer.price == 0) {
            this.errors.push("desiredPrice");
        }
        if (this.desiredStartTerm.value == null || this.desiredStartTerm.value.length == 0) {
            this.errors.push("desiredStartTerm");
        }
        if (this.desiredEndTerm.value == null || this.desiredEndTerm.value.length == 0) {
            this.errors.push("desiredEndTerm");
        }
        if (this.errors.length > 0) {
            this.showError = true;
            this.error = "Please, populate desired price and terms.";
            return;
        }
        if (this.desiredEndTerm.value <= this.desiredStartTerm.value) {
            this.error = "Your service ending date is earlier than service starting date.";
            return;
        }
        if (DateTime.fromJSDate(this.desiredStartTerm.value) < this.currentDate.plus({ days: 14 })) {
            this.showError = true;
            this.error = "Your starting date shall be at least 14 days after today.";
            return;
        }
        if (this.buyOffer.id > 0 && (this.desiredStartTerm.value < DateTime.fromJSDate(this.buyOffer.validDate))) {
            this.showError = true;
            this.error = "Your starting date is earlier than valid date of the order.";
            return;
        }
        this.buyOffer.termFrom = this.desiredStartTerm.value.toJSDate();
        this.error = "";
        this.showError = false;
        this.step = 3;
    }

    finishStep3(): void {
        if (this.durationOfOrder == null || this.durationOfOrder.value == null) {
            this.errors.push("durationOfOrder");
        }
        if (this.errors.length > 0) {
            this.showError = true;
            this.error = "Please, populate offer duration.";
            return;
        }

        var ctrlValue = this.desiredStartTerm.value;
        ctrlValue = ctrlValue.set({ day: 1 });
        this.desiredStartTerm.setValue(ctrlValue);
        this.buyOffer.termFrom = this.desiredStartTerm.value;

        var subCurrentDate = this.currentDate;
        if (this.buyOffer.id == 0 && (DateTime.fromJSDate(this.buyOffer.termFrom) < (this.durationOfOrder.value))) {
            this.showError = true;
            this.error = "Your starting date shall be ahead of the valid date of the order.";
            return;
        }
        this.durationOfOrder.setValue(this.durationOfOrder.value.setZone("America/New_York", { keepLocalTime: true }));
        this.durationOfOrder.setValue(this.durationOfOrder.value.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }));

        if (this.buyOffer.id > 0) {
            this.buyOffer.validDate = this.durationOfOrder.value.toJSDate();
        } else if (this.durationOfOrder != null && this.durationOfOrder.value != null) {
            this.buyOffer.validDate = this.durationOfOrder.value.toJSDate();
        } else {
            ctrlValue = ctrlValue.minus({ days: 3 });
            if (ctrlValue < this.currentDate) {
                ctrlValue = this.currentDate;
            }
            this.buyOffer.validDate = ctrlValue.toJSDate();
        }
        this.processReview = true;
        this.step = 4;
    }

    cancel(): void {
        this.cancelled.next(true);
    }

    sendForm(): void {
        if (this.buyOffer.id > 0) {
            this.buyOffer.accounts = this.selectedAccounts;
            this.generalService.updateBuyOffer(this.buyOffer)
                .subscribe((res: any) => {
                    if (res) {
                        this.finished.next(true);
                    }
                },
                    err => {
                        if (err.status == 400) {
                            this.showError = true;
                            this.error = err.error;
                        }
                    });
        } else {
            this.generalService.addBuyOffer(this.selectedAccounts, this.buyOffer)
                .subscribe((res: any) => {
                    if (res) {
                        this.finished.next(true);
                    }
                },
                    err => {
                        if (err.status == 400) {
                            this.showError = true;
                            this.error = err.error;
                        }
                    });
        }
    }

    resetError(field: string): void {
        if (this.errors.includes(field)) {
            this.errors.splice(this.errors.indexOf(field), 1);
        }
        this.showError = (this.errors.length > 0);
    }

    addRemoveAccount(account: Account, adding: boolean): void {
        if (adding && !this.ifItemSelected(account.id)) {
            this.selectedAccounts.push(account);
        }
        if (!adding) {
            EntityBase.removeValueFromList(this.selectedAccounts, account.id);
        }
        if (this.selectedAccounts.length > 0) {
            this.generalService.getAccountCurrentCosts(this.selectedAccounts[0].id).then(c => {
                this.currentCost = c[0];
            });
        } else {
            this.currentCost = null;
            this.loadZone = null;
        }
        this.resetError("selectedAccounts");
        var x = "";
        for (var i = 0; i < this.selectedAccounts.length; i++) {
            x = x + ":" + this.selectedAccounts[i].id;
        }
    }

    resetSelectedAccounts(): void {
        this.selectedAccounts = [];
    }

    accountIncludes(account: Account): number {
        return EntityBase.indexOf(this.selectedAccounts, account);
    }

    changeChart(): void {
        if (this.step < 2) {
            return;
        }
        if (this.buyOffer.termPeriod / 3 >= 12) {
            this.disabledButtons = "1,2";
        } else if (this.buyOffer.termPeriod > 12) {
            this.disabledButtons = "1";
        } else {
            this.disabledButtons = "";
        }
        const ctrlValue = this.desiredStartTerm.value;
        ctrlValue.set({ day: 1 });
        this.desiredStartTerm.setValue(ctrlValue);
        this.generalService.getRates(this.desiredStartTerm.value, null, this.buyOffer.termPeriod, this.selectedAccounts)
            .then(d => {
                this.multi = d;
                this.ratesAveragePrice = this.generalService.getAverageForRates(this.multi);
                this.setPrice();
            });
    }

    changeVal(val?: number): void {
        if (val != null && val > 0) {
            this.buyOffer.price = val;
            this.setBenchmarkOfferCustom();
        }
    }

    omit_non_used_chars(e: any): boolean {
        var k;
        k = (<KeyboardEvent>event).charCode;
        return ((k >= 48 && k <= 57) || (k >= 44 && k <= 45) || (k == 32));
    }

    handleFileInput(event: Event) {
        const target = event.target as HTMLInputElement;
        this.filesToUpload = target.files as FileList;
    }

    setDesiredTerm(): void {
        if (this.desiredStartTerm == null || this.desiredStartTerm.value == null ||
            this.desiredEndTerm == null || this.desiredEndTerm.value == null
        ) {
            this.desiredTerm = null;
            this.buyOffer.termPeriod = 0;
            return;
        }
        var diff = this.desiredEndTerm.value.diff(this.desiredStartTerm.value, ["months"]);
        var mnth = Math.floor(diff["months"]);
        if (mnth >= 0) {
            this.desiredTerm = mnth + " months";
            this.buyOffer.termPeriod = mnth;
        } else {
            this.desiredTerm = null;
            this.buyOffer.termPeriod = 0;
        }
        this.setDefaultDuration(this.desiredStartTerm.value);
        this.changeChart();
    }

    inactiveDatesFilter(d: DateTime | null): boolean {
        if (d == null || this.globalStart == null || this.globalEnd == null) {
            return false;
        }
        if (d.day != 1) {
            return false;
        }
        return (d > this.globalStart) && (d < this.globalEnd);
    }

    inactiveDatesEndFilter(d: DateTime | null): boolean {
        if (d == null) {
            return false;
        }
        if (this.desiredStartTerm != null && this.desiredStartTerm.value != null) {
            if (d < this.desiredStartTerm.value) {
                return false;
            }
        }
        return this.inactiveDatesFilter(d);
    }

    inactiveDatesGoodUntilFilter(d: DateTime | null): boolean {
        if (d == null) {
            return false;
        }
        if (this.desiredStartTerm != null && this.desiredStartTerm.value != null) {
            if (d > this.desiredStartTerm.value.minus({ days: 3 })) {
                return false;
            }
        }
        return (d > this.currentDate);
    }

    setDefaultDuration(cValue: DateTime): void {
        cValue = cValue.minus({ days: 3 });
        if (cValue < this.currentDate) {
            cValue = this.currentDate;
        }
        this.durationOfOrder.setValue(cValue);
    }

    setPrice(): void {
        if (this.benchmarkType != null && this.benchmarkType != "none") {
            var price = 0;
            var benchmarkData = this.benchmarkType.split("-");
            var discount = 0;
            if (benchmarkData.length > 1) {
                discount = parseInt(benchmarkData[1]);
            }
            if (benchmarkData[0] == "sell_offer" && this.benchmarkOffer != null) {
                price = this.benchmarkOffer.priceForBuyer();
            } else if (benchmarkData[0] == "contract") {

            } else if (benchmarkData[0] == "utility") {
                price = this.getDefaultPriceBasedOnUtility();
                if (price == 0 && this.latestUtility != null) {
                    price = this.latestUtility;
                }
            } else {
                price = 0;
            }
            if (price > 0 && discount > 0 && discount < 100) {
                price = price * ((100 - discount) / 100);
            }
            this.buyOffer.price = price;
        }
    }

    getDefaultPriceBasedOnUtility(): number {
        var avg = [];
        var cnt = [];
        var price = 0;
        for (var i = 0; i < this.multi.length; i++) {
            for (var x = 0; x < this.multi[i].series.length; x++) {
                if (avg.length == x) {
                    avg.push(0);
                    cnt.push(0);
                }
                avg[x] = avg[x] + Number(this.multi[i].series[x].value);
                cnt[x]++;
            }
        }
        for (var i = 0; i < avg.length; i++) {
            if (price == 0) {
                price = avg[i] / cnt[i];
            } else if (this.buyOffer.price > avg[i] / cnt[i]) {
                price = avg[i] / cnt[i];
            }
        }
        return price;
    }

    setPriceLatestUtilityRate(): void {
        this.generalService.getLatestRate(this.selectedAccounts).then(r => {
            this.latestUtility = r;
        });
    }

    checkIfUtilityEmpty(): boolean {
        if (this.latestUtility != null && this.latestUtility > 0) {
            return false;
        }
        for (var i = 0; i < this.multi.length; i++) {
            for (var x = 0; x < this.multi[i].series.length; x++) {
                if (Number(this.multi[i].series[x].value) > 0) {
                    return false;
                }
            }
        }
        return true;
    }

    getCurrentContract(): void {
        this.generalService.getContracts(this.selectedAccounts[0], false, null, true).then(contracts => {
            if (contracts.length > 0) {
                this.benchmarkContract = contracts[0];
            }
        });
    }

    setBenchmarkOfferCustom(): void {
        this.benchmarkType = "none";
    }

    ifItemSelected(id: number): boolean {
        for (let i = 0; i < this.selectedAccounts.length; i++) {
            if (this.selectedAccounts[i].id == id) {
                return true;
            }
        }
        return false;
    }

    selectItem(id: number): void {
        var a;
        for (var i = 0; i < this.accounts.length; i++) {
            if (this.accounts[i].id == id) {
                a = this.accounts[i];
                break;
            }
        }
        if (a != undefined) {
            if (this.ifItemSelected(a.id)) {
                this.addRemoveAccount(a, false);
            } else {
                this.addRemoveAccount(a, true);
            }
        }
    }

    changeDataset(pos: number): void {
        if (this.selectedDatasets.includes(pos) && this.selectedDatasets.length > 1) {
            this.selectedDatasets.splice(this.selectedDatasets.indexOf(pos), 1);
            this.selectedDatasets.includes(pos);
        } else if (!this.selectedDatasets.includes(pos)) {
            this.selectedDatasets.push(pos);
        }
        this.activeRates = [];
        for (let x = 0; x < this.multi.length; x++) {
            let r = new RateData(this.multi[x].name, []);
            for (let y = 0; y < this.multi[x].series.length; y++) {
                if (this.selectedDatasets.includes((y + 1))) {
                    r.series.push(this.multi[x].series[y]);
                }
            }
            this.activeRates.push(r);
        }
    }

    countRates(): number {
        if (this.multi.length == 0) {
            return 0;
        }
        var names: string[] = [];
        for (let i = 0; i < this.multi.length; i++) {
            if (this.multi[i].series != null || this.multi[i].series.length > 0) {
                for (let p = 0; p < this.multi[p].series.length; p++) {
                    if (!names.includes(this.multi[i].series[p].name)) {
                        names.push(this.multi[i].series[p].name);
                    }
                }
                return names.length;
            }
        }
        return 0;
    }



    private automaticallySelectAccounts(selectedAccountsIdsArr: number[]) {
        this.resetSelectedAccounts();
        for (var i = 0; i < selectedAccountsIdsArr.length; i++) {
            for (var x = 0; x < this.accounts.length; x++) {
                if (this.accounts[x].id == selectedAccountsIdsArr[i]) {
                    this.addRemoveAccount(this.accounts[x], true);
                }
            }
        }
    }

    boundInactiveDatesFilter = this.inactiveDatesFilter.bind(this);
    boundInactiveDatesEndFilter = this.inactiveDatesEndFilter.bind(this);
    boundInactiveDatesGoodUntilFilter = this.inactiveDatesGoodUntilFilter.bind(this);

}